import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Plus, Trash2, Search, Shield, Edit, Phone, Mail, Key 
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const SubSuperAdminsPage = () => {
  const { user } = useAuth();
  const [subAdmins, setSubAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editAdmin, setEditAdmin] = useState(null);
  const [deleteAdmin, setDeleteAdmin] = useState(null);
  const [newAdmin, setNewAdmin] = useState({ 
    name: "", phone: "", email: "", password: "", auth_type: "phone" 
  });

  useEffect(() => { fetchSubAdmins(); }, []);

  const fetchSubAdmins = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/sub-superadmins`);
      setSubAdmins(res.data);
    } catch (err) {
      toast.error("Failed to load sub-super-admins");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newAdmin.name) {
      toast.error("Name is required");
      return;
    }
    if (newAdmin.auth_type === "phone" && !newAdmin.phone) {
      toast.error("Phone is required for phone authentication");
      return;
    }
    if (newAdmin.auth_type === "email" && (!newAdmin.email || !newAdmin.password)) {
      toast.error("Email and password are required for email authentication");
      return;
    }

    try {
      await axios.post(`${API}/superadmin/sub-superadmins?created_by=${user?.id || 'superadmin'}`, newAdmin);
      toast.success("Sub-Super-Admin created successfully");
      setShowAddDialog(false);
      setNewAdmin({ name: "", phone: "", email: "", password: "", auth_type: "phone" });
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create sub-super-admin");
    }
  };

  const handleUpdate = async () => {
    if (!editAdmin) return;
    try {
      await axios.put(`${API}/superadmin/sub-superadmins/${editAdmin.id}`, {
        name: editAdmin.name,
        phone: editAdmin.phone,
        email: editAdmin.email,
        auth_type: editAdmin.auth_type,
      });
      toast.success("Sub-Super-Admin updated");
      setEditAdmin(null);
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  };

  const handleToggleStatus = async (admin) => {
    try {
      const newStatus = !admin.is_active;
      await axios.put(`${API}/superadmin/sub-superadmins/${admin.id}/status?is_active=${newStatus}`);
      toast.success(`Sub-Super-Admin ${newStatus ? 'enabled' : 'disabled'}`);
      fetchSubAdmins();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    if (!deleteAdmin) return;
    try {
      await axios.delete(`${API}/superadmin/sub-superadmins/${deleteAdmin.id}`);
      toast.success("Sub-Super-Admin deleted");
      setDeleteAdmin(null);
      fetchSubAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const filteredAdmins = subAdmins.filter(admin =>
    admin.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    admin.phone?.includes(searchTerm)
  );

  const getAuthIcon = (authType) => {
    switch (authType) {
      case "phone": return <Phone className="w-4 h-4" />;
      case "email": return <Mail className="w-4 h-4" />;
      case "google": return <Key className="w-4 h-4" />;
      default: return <Phone className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="sub-super-admins-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Sub-Super-Admins</h2>
          <p className="text-muted-foreground">
            {subAdmins.length} sub-super-admin{subAdmins.length !== 1 ? 's' : ''} with elevated privileges
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-sub-superadmin-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Sub-Super-Admin
        </Button>
      </div>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          placeholder="Search by name, email, or phone..." 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
          className="pl-10" 
        />
      </div>

      {/* Info Banner */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <p className="font-medium text-purple-800">Sub-Super-Admin Permissions</p>
            <p className="text-sm text-purple-600">
              Sub-Super-Admins have the same permissions as Super Admin, except they cannot delete the primary Super Admin account.
              They can create and manage other Sub-Super-Admins.
            </p>
          </div>
        </div>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3">
        {filteredAdmins.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No sub-super-admins found</div>
        ) : (
          filteredAdmins.map((admin) => (
            <div key={admin.id} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium">{admin.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {getAuthIcon(admin.auth_type)}
                      {admin.auth_type === "phone" ? admin.phone : admin.email}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditAdmin(admin)}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => setDeleteAdmin(admin)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground capitalize">Auth: {admin.auth_type}</span>
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={admin.is_active !== false} 
                    onCheckedChange={() => handleToggleStatus(admin)} 
                  />
                  <span className={`text-xs ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                    {admin.is_active !== false ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Auth Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdmins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No sub-super-admins found
                </TableCell>
              </TableRow>
            ) : (
              filteredAdmins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-purple-600" />
                      </div>
                      <span className="font-medium">{admin.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {admin.phone && <p>{admin.phone}</p>}
                      {admin.email && <p className="text-muted-foreground text-xs">{admin.email}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded text-xs capitalize">
                      {getAuthIcon(admin.auth_type)}
                      {admin.auth_type}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={admin.is_active !== false} 
                        onCheckedChange={() => handleToggleStatus(admin)} 
                      />
                      <span className={`text-sm ${admin.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                        {admin.is_active !== false ? 'Active' : 'Disabled'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setEditAdmin(admin)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => setDeleteAdmin(admin)} 
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Sub-Super-Admin</DialogTitle>
            <DialogDescription>
              Create a new sub-super-admin with elevated privileges
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={newAdmin.name} 
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} 
                placeholder="Full Name" 
              />
            </div>
            <div>
              <Label>Authentication Type *</Label>
              <Select 
                value={newAdmin.auth_type} 
                onValueChange={(v) => setNewAdmin({ ...newAdmin, auth_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="phone">Phone OTP</SelectItem>
                  <SelectItem value="email">Email & Password</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {newAdmin.auth_type === "phone" && (
              <div>
                <Label>Phone Number *</Label>
                <Input 
                  value={newAdmin.phone} 
                  onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })} 
                  placeholder="10-digit phone number" 
                />
              </div>
            )}
            {newAdmin.auth_type === "email" && (
              <>
                <div>
                  <Label>Email *</Label>
                  <Input 
                    type="email"
                    value={newAdmin.email} 
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} 
                    placeholder="email@example.com" 
                  />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input 
                    type="password"
                    value={newAdmin.password} 
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} 
                    placeholder="Enter password" 
                  />
                </div>
              </>
            )}
            <div>
              <Label>Email (Optional for Phone Auth)</Label>
              <Input 
                type="email"
                value={newAdmin.email} 
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} 
                placeholder="email@example.com (optional)" 
                disabled={newAdmin.auth_type === "email"}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editAdmin} onOpenChange={(open) => !open && setEditAdmin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sub-Super-Admin</DialogTitle>
          </DialogHeader>
          {editAdmin && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input 
                  value={editAdmin.name} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, name: e.target.value })} 
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input 
                  value={editAdmin.phone || ""} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, phone: e.target.value })} 
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editAdmin.email || ""} 
                  onChange={(e) => setEditAdmin({ ...editAdmin, email: e.target.value })} 
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAdmin(null)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAdmin} onOpenChange={() => setDeleteAdmin(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sub-Super-Admin</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteAdmin?.name}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SubSuperAdminsPage;
